import { useEffect, useState } from 'react';
import type { PerfTier } from '../types';

/**
 * Reads the performance tier set by the boot-time detector.
 *
 * The detector runs before React mounts, but may finish after — so this
 * seeds from `window.__PERF_TIER__` and also listens for `perftierready`.
 * Defaults to `high` when the detector has not reported yet.
 */
export function usePerfTier(): PerfTier {
  const [tier, setTier] = useState<PerfTier>(() => window.__PERF_TIER__ ?? 'high');

  useEffect(() => {
    const handle = (event: WindowEventMap['perftierready']): void => {
      setTier(event.detail?.tier ?? window.__PERF_TIER__ ?? 'high');
    };

    window.addEventListener('perftierready', handle);

    // Pick up a tier set between the initial state and this effect running.
    const current = window.__PERF_TIER__;
    if (current !== undefined) setTier(current);

    return () => window.removeEventListener('perftierready', handle);
  }, []);

  return tier;
}
